/**
 * AdminCollectionsUserDetailPage — /admin/collections/:userId
 *
 * Admin drill-through from CollectionsAdmin leaderboard. Shows one
 * staff member's full outstanding list (highest → lowest) + their
 * action log for the current snapshot.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, IndianRupee, Trash2 } from 'lucide-react';
import { api, formatINR, useAuth } from '../lib/store';
import { logger } from '../lib/logger';
import { toast } from 'sonner';

const ACTION_LABEL = {
  called: 'Called', promised: 'Promised', partial_payment: 'Partial pay',
  payment_received: 'Payment received', visited: 'Visited',
  cheque_requested: 'Cheque requested', no_response: 'No response', note: 'Note',
};

export default function AdminCollectionsUserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = ['admin', 'manager'].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.getCollectionsUserDetail(userId)
      .then(r => setData(r.data))
      .catch(e => logger.warn('Failed to load:', e))
      .finally(() => setLoading(false));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const deleteRow = async (row) => {
    if (!window.confirm(
      `Remove ${row.customer_name} (${formatINR(row.amount)}) from this snapshot?\n\n` +
      `This deletes the customer row and any actions you've logged against them. It cannot be undone.`
    )) return;
    setDeletingId(row.id);
    try {
      await api.deleteCollectionRow(row.id);
      toast.success(`Removed ${row.customer_name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete row');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <div className="p-6"><Skeleton className="h-96 w-full" /></div>;
  if (!data) return <div className="p-6 text-[#8F9485]">Not found</div>;

  const total = (data.rows || []).reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4" data-testid="admin-collections-detail">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="collections-detail-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Collections
        </Button>
      </div>

      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-[#1A1C16]">
          {data.user?.name || 'Salesperson'}
          {data.user?.staff_code && <span className="ml-2 text-sm font-mono bg-[#556B2F]/10 text-[#556B2F] px-2 py-0.5 rounded">{data.user.staff_code}</span>}
        </h1>
        <p className="text-sm text-[#5C6150] mt-0.5">
          {(data.rows || []).length} outstanding customers · <span className="font-semibold text-[#556B2F]">{formatINR(total)}</span> pending
        </p>
      </div>

      <Card className="border-[#E2E6D9]">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-[#556B2F]" /> Outstanding Customers (highest first)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto rounded-md border border-[#E2E6D9]">
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAF7] text-[10px] uppercase tracking-wider text-[#5C6150]">
                <tr>
                  <th className="text-left py-2 px-3">#</th>
                  <th className="text-left py-2 px-3">Customer</th>
                  <th className="text-left py-2 px-3">Mobile</th>
                  <th className="text-right py-2 px-3">Amount</th>
                  {canDelete && <th className="text-right py-2 px-3 w-16"></th>}
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((r, i) => (
                  <tr key={r.id} className="border-t border-[#E2E6D9] hover:bg-[#F9FAF7]" data-testid={`admin-detail-row-${r.id}`}>
                    <td className="py-1.5 px-3 text-[#8F9485]">{i + 1}</td>
                    <td className="py-1.5 px-3 font-medium">{r.customer_name}</td>
                    <td className="py-1.5 px-3 font-mono text-[#5C6150]">{r.customer_mobile || '—'}</td>
                    <td className="py-1.5 px-3 text-right font-mono-data font-semibold text-[#556B2F]">{formatINR(r.amount)}</td>
                    {canDelete && (
                      <td className="py-1.5 px-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteRow(r)}
                          disabled={deletingId === r.id}
                          className="h-6 px-1.5 text-[#B93838] hover:bg-[#FDECEA]"
                          data-testid={`row-delete-btn-${r.id}`}
                          title="Remove this customer from the snapshot"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {(data.actions || []).length > 0 && (
        <Card className="border-[#E2E6D9]">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wide">Action Log ({data.actions.length})</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1 text-xs">
              {data.actions.map(a => (
                <div key={a.id} className="border-b border-[#E2E6D9] py-1.5 flex items-center gap-2 flex-wrap" data-testid={`admin-detail-action-${a.id}`}>
                  <Badge variant="outline" className="text-[10px] py-0">{ACTION_LABEL[a.action] || a.action}</Badge>
                  <span className="font-medium">{a.customer_name}</span>
                  {a.amount ? <span className="font-mono-data text-[#556B2F] font-semibold">{formatINR(a.amount)}</span> : null}
                  {a.promise_date ? <span className="text-[#8B6914]">promised by {a.promise_date}</span> : null}
                  {a.note ? <span className="text-[#5C6150] italic">&ldquo;{a.note}&rdquo;</span> : null}
                  <span className="ml-auto text-[10px] text-[#8F9485]">{new Date(a.created_at).toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
